import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import PropTypes from 'prop-types'

import Loader from '../../components/Loader'

import PublicRoute from './guards/PublicRoute'
import PrivateRoute from './guards/PrivateRoute'
import RegistrationRoute from './guards/RegistrationRoute'
import SwitchRoleRoute from './guards/SwitchRoleRoute'

import PublicRoutes from './Public'
import RegistrationRoutes from './Registration'
import SwitchRoleRoutes from './SwitchRole'
import { SellerRoutes } from './Seller'
import { BuyerRoutes } from './Buyer'

const DefaultLayout = lazy(() => import('../layout/DefaultLayout'))
const PageNotFound = lazy(() => import('../pages/PageNotFound'))
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'))

const renderRoutes = (routes) =>
  routes.map((route, index) => (
    <Route key={index} path={route.path} element={route.element} />
  ))

const buyerPaths = new Set(BuyerRoutes.map((r) => r.path))
const sellerPaths = new Set(SellerRoutes.map((r) => r.path))
const sellerOnlyRoutes = SellerRoutes.filter((r) => !buyerPaths.has(r.path))
const buyerOnlyRoutes = BuyerRoutes.filter((r) => !sellerPaths.has(r.path))

const getAuthenticatedRoutes = (role) => {
  if (role === 'seller') return SellerRoutes
  if (role === 'buyer') return BuyerRoutes
  return [...SellerRoutes, ...BuyerRoutes]
}

const getRoleFallbackRoutes = (role) => {
  if (role === 'buyer') {
    return sellerOnlyRoutes.map((route) => (
      <Route
        key={`fallback-${route.path}`}
        path={route.path}
        element={<Navigate to='/' replace />}
      />
    ))
  }
  if (role === 'seller') {
    return buyerOnlyRoutes.map((route) => (
      <Route
        key={`fallback-${route.path}`}
        path={route.path}
        element={<Navigate to='/' replace />}
      />
    ))
  }
  return null
}

const AppRoutes = ({ userRole }) => (
  <Suspense fallback={<Loader height />}>
    <Routes>
      <Route path='/*' element={<PageNotFound />} />
      <Route path='/privacy-policy' element={<PrivacyPolicy />} />

      {/* Public Auth Routes */}
      <Route element={<PublicRoute />}>{renderRoutes(PublicRoutes)}</Route>

      {/* Registration Flow */}
      <Route element={<RegistrationRoute />}>
        {renderRoutes(RegistrationRoutes)}
      </Route>

      {/* Authenticated Routes */}
      <Route element={<PrivateRoute />}>
        <Route element={<DefaultLayout />}>
          <Route element={<SwitchRoleRoute />}>
            {renderRoutes(SwitchRoleRoutes)}
          </Route>
          {renderRoutes(getAuthenticatedRoutes(userRole))}
          {getRoleFallbackRoutes(userRole)}
          <Route path='*' element={<Navigate to='/' replace />} />
        </Route>
      </Route>
    </Routes>
  </Suspense>
)

AppRoutes.propTypes = {
  userRole: PropTypes.string,
}

export default AppRoutes
